import { z } from "zod";

/**
 * Canonical entity-URN primitives, shared by the backend and the web client
 * (this dir is symlinked into `web/src/types`). Pure — depends only on Zod.
 *
 * Canonical form is `urn:bb:<type>:<id>`. The bare `type:id` short form is not a
 * URN and is rejected here.
 */

/**
 * A canonical entity URN with a numeric id: `urn:bb:<type>:<id>`. Multi-segment
 * ids (e.g. `urn:bb:routine:system:daily-briefing`) are still parseable via
 * `parseURN`/`safeParseURN`; this stricter schema gates the link/markdown paths,
 * which only ever address numeric-id ents (sources, contacts, …).
 */
const ENTITY_URN_RE = /^urn:bb:[a-z]+:\d+$/i;

/** A canonical entity-URN string (`urn:bb:<type>:<id>`). */
export const urnSchema = z.string().max(256).regex(ENTITY_URN_RE);

/**
 * A URN whose id is a UUID or carries segments — `urn:bb:routine:<uuid>` and
 * `urn:bb:routine:system:daily-briefing` both qualify.
 *
 * Deliberately separate from {@link urnSchema} rather than a relaxation of it.
 * The strict form gates link/markdown paths, where a non-loadable id is a bug
 * worth rejecting early; the canvas additionally presents entities those paths
 * never address, and routines are keyed by UUID or slug rather than a numeric
 * ent id. Widening the strict schema would quietly re-admit `urn:bb:source:…`
 * shapes it exists to reject.
 */
const SEGMENTED_URN_RE = /^urn:bb:[a-z]+:[A-Za-z0-9][A-Za-z0-9:_-]*$/i;

/** A URN string permitting a UUID or segmented id. */
export const segmentedUrnSchema = z.string().max(256).regex(SEGMENTED_URN_RE);

/** Whether `value` is a URN with a UUID or segmented id. */
export const isSegmentedUrn = (value: unknown): value is string =>
  segmentedUrnSchema.safeParse(value).success;

/** The parsed parts of a `urn:bb:<type>:<id>` string. */
export type URN = { namespace: string; type: string; id: string };

/**
 * Parse a URN in the format `urn:bb:<type>:<id>`. The id is everything after the
 * type, so ids that themselves contain colons (e.g. the `system:<id>` sentinel
 * in `urn:bb:routine:system:daily-briefing`) survive intact. Throws for a
 * non-`urn:` string or a namespace other than `bb`.
 */
export const parseURN = (arg: string): URN => {
  if (!arg.startsWith("urn:")) {
    throw new Error("URN must start with 'urn:'");
  }
  const urn = arg.slice(4);
  const [namespace, type, ...rest] = urn.split(":");

  if (namespace !== "bb") {
    throw new Error(`URN must be in the 'bb' namespace, got ${namespace}`);
  }

  return { namespace, type, id: rest.join(":") };
};

/** Parse something that might be a URN; returns null instead of throwing. */
export const safeParseURN = (arg: string): URN | null => {
  if (!arg.startsWith("urn:")) {
    return null;
  }
  const urn = arg.slice(4);
  const [namespace, type, ...rest] = urn.split(":");

  if (namespace !== "bb") {
    return null;
  }

  return { namespace, type, id: rest.join(":") };
};

/** Format a `{type, id}` pair into a canonical `urn:bb:<type>:<id>` string (inverse of {@link parseURN}). */
export const toUrnString = (urn: Pick<URN, "type" | "id">): string => {
  return `urn:bb:${urn.type}:${urn.id}`;
};

/**
 * True for a canonical entity-URN string (e.g. `urn:bb:foo:123`), bounded in
 * length. The shape contract lives in {@link urnSchema}.
 */
export const isUrn = (value: string): boolean => urnSchema.safeParse(value).success;
